"use client"

/**
 * SwitchAccountsDialog — multi-account picker for the device.
 *
 * Shown by NavUser when the user clicks "Switch Accounts". Lists every
 * authenticated identity stored on this device (max 4), with an avatar
 * grid + name/email below each. Click avatar → switch active. X on
 * avatar → sign that account out of the device (with confirmation).
 * "+" tile → redirect to the login app with `?add_account=1` so the
 * returned token augments rather than replaces the active identity.
 *
 * Plan reference: nav-user / global multi-account UX from PR feedback.
 */

import { useEffect, useState } from "react"
import { Plus, X, LogOut, Check } from "lucide-react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Button } from "../ui/button"
import {
    AccountEntry,
    MAX_ACCOUNTS,
    buildAddAccountUrl,
    getAccounts,
    getActiveAccountId,
    removeAccount,
    setActiveAccount,
} from "../lib/multi-account"
import { getLoginAppUrl } from "../lib/auth"

function getInitials(name?: string) {
    if (!name) return "U"
    const parts = name.split(" ").filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    return name.substring(0, 2).toUpperCase()
}

interface SwitchAccountsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function SwitchAccountsDialog({ open, onOpenChange }: SwitchAccountsDialogProps) {
    const [accounts, setAccounts] = useState<AccountEntry[]>([])
    const [activeId, setActiveId] = useState<string | null>(null)
    const [confirmRemove, setConfirmRemove] = useState<AccountEntry | null>(null)
    const [busy, setBusy] = useState(false)

    const refresh = () => {
        if (typeof window === "undefined") return
        setAccounts(getAccounts())
        setActiveId(getActiveAccountId())
    }

    useEffect(() => {
        if (open) refresh()
    }, [open])

    const handleSwitch = (acc: AccountEntry) => {
        if (acc.id === activeId) {
            onOpenChange(false)
            return
        }
        setBusy(true)
        const ok = setActiveAccount(acc.id)
        if (ok && typeof window !== "undefined") {
            // Reload to refresh React state across the whole app
            setTimeout(() => window.location.reload(), 100)
        } else {
            setBusy(false)
        }
    }

    const handleAdd = () => {
        if (typeof window === "undefined") return
        if (accounts.length >= MAX_ACCOUNTS) return
        const returnTo = window.location.href
        const url = buildAddAccountUrl(getLoginAppUrl(), returnTo)
        window.location.href = url
    }

    const handleRemove = async (acc: AccountEntry) => {
        setBusy(true)
        const result = await removeAccount(acc.id)
        setConfirmRemove(null)
        if (result.removed) {
            if (result.nextActiveId === null && typeof window !== "undefined") {
                // Last account removed — full logout
                window.location.href = getLoginAppUrl() + "/auth/login?loggedOut=true"
                return
            }
            // Refresh state — if we removed the active and switched, reload
            if (acc.id === activeId) {
                if (typeof window !== "undefined") {
                    setTimeout(() => window.location.reload(), 100)
                    return
                }
            }
            refresh()
        }
        setBusy(false)
    }

    const fullCount = accounts.length
    const slots = MAX_ACCOUNTS - fullCount

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o) }}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Switch accounts</DialogTitle>
                        <DialogDescription>
                            Up to {MAX_ACCOUNTS} accounts can be signed in to this device.
                            Click an avatar to switch active. Click <span className="inline-block align-middle"><X className="inline h-3 w-3" /></span> to sign out of this device.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4">
                        {accounts.map((acc) => {
                            const isActive = acc.id === activeId
                            return (
                                <div
                                    key={acc.id}
                                    className={`relative group rounded-xl border p-4 flex flex-col items-center gap-2 transition-colors cursor-pointer ${
                                        isActive ? "border-foreground bg-accent" : "border-border hover:border-foreground/30 hover:bg-accent/40"
                                    }`}
                                >
                                    <button
                                        type="button"
                                        aria-label={`Sign out ${acc.user.email ?? "account"}`}
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemove(acc) }}
                                        className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleSwitch(acc)}
                                        disabled={busy}
                                        className="flex flex-col items-center gap-2 w-full focus:outline-none disabled:opacity-50"
                                    >
                                        <div className="relative">
                                            <Avatar className="h-16 w-16 rounded-full bg-white">
                                                <AvatarImage src={acc.user.avatar || ""} alt={acc.user.name || ""} className="object-cover w-full h-full rounded-full" />
                                                <AvatarFallback className="rounded-full text-base">{getInitials(acc.user.name)}</AvatarFallback>
                                            </Avatar>
                                            {isActive && (
                                                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-background flex items-center justify-center">
                                                    <Check className="h-3 w-3 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-center w-full overflow-hidden">
                                            <div className="text-sm font-medium truncate">{acc.user.name || "User"}</div>
                                            <div className="text-xs text-muted-foreground truncate">{acc.user.email || ""}</div>
                                        </div>
                                    </button>
                                </div>
                            )
                        })}

                        {/* Add-account tile */}
                        {slots > 0 && (
                            <button
                                type="button"
                                onClick={handleAdd}
                                disabled={busy}
                                className="rounded-xl border border-dashed border-border hover:border-foreground/40 hover:bg-accent/40 transition-colors p-4 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground focus:outline-none disabled:opacity-50 min-h-[140px]"
                            >
                                <div className="h-16 w-16 rounded-full border-2 border-dashed border-border flex items-center justify-center">
                                    <Plus className="h-6 w-6" />
                                </div>
                                <div className="text-center">
                                    <div className="text-sm font-medium">Add account</div>
                                    <div className="text-xs">{slots === 1 ? "1 slot left" : `${slots} slots left`}</div>
                                </div>
                            </button>
                        )}
                    </div>

                    <p className="text-[11px] text-muted-foreground text-center">
                        Active account is shared across all askFinz subdomains.
                        Other tabs may need a refresh after switching.
                    </p>
                </DialogContent>
            </Dialog>

            {/* Confirm-remove inner dialog */}
            <Dialog open={!!confirmRemove} onOpenChange={(o) => { if (!o && !busy) setConfirmRemove(null) }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Sign out of this device?</DialogTitle>
                        <DialogDescription>
                            {confirmRemove?.user.email ? <><strong>{confirmRemove.user.email}</strong> will be signed out of this device.</> : "This account will be signed out of this device."}
                            {" "}You can sign in again at any time.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(null)} disabled={busy}>
                            Cancel
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => confirmRemove && handleRemove(confirmRemove)} disabled={busy}>
                            <LogOut className="h-4 w-4 mr-1.5" />
                            {busy ? "Signing out…" : "Sign out"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
